/**
 * Universal Logger System Types
 * 
 * TypeScript interfaces and types for the Universal Logger System.
 * Provides type safety for logger configuration, log entries, and API methods.
 */

// Re-export correlation ID types for convenience
export type { 
  CorrelationIdOptions, 
  CorrelationIdContext 
} from './correlation-id';

/**
 * Universal log levels
 */
export enum UniversalLogLevel {
  ERROR = 'error',
  WARN = 'warn',
  INFO = 'info',
  DEBUG = 'debug'
}

/**
 * Namespace types for domain-specific loggers
 */
export enum LogNamespace {
  AUTH = 'auth',
  API = 'api',
  UI = 'ui',
  PERFORMANCE = 'performance',
  DATABASE = 'database',
  CLASSIFICATION = 'classification',
  JUDGE = 'judge',
  STORAGE = 'storage',
  WORKER = 'worker',
  METRICS = 'metrics',
  SECURITY = 'security'
}

/**
 * Performance metrics for log entries
 */
export interface PerformanceMetrics {
  // Timing information
  duration?: number;
  startTime?: number;
  endTime?: number;
  
  // Memory information
  memoryUsage?: number; // Basic memory usage (bytes)
  memoryUsageDetails?: {
    used: number;
    usedMB: number;
    percentage: number;
    timestamp: number;
  };
  
  // Memory delta tracking (for operations)
  memoryDelta?: {
    usedDelta: number;
    usedDeltaMB: number;
    timeDelta: number;
  };
  
  // Performance alerts and warnings
  memoryWarning?: MemoryThresholdCheck;
  memoryIncrease?: string; // Warning message for significant memory increases
  
  // Operation context
  operationName?: string;
  functionName?: string;
  status?: 'success' | 'error' | 'warning';
  async?: boolean;
}

/**
 * Universal log entry interface
 */
export interface UniversalLogEntry {
  // Core identification
  timestamp: string;
  level: UniversalLogLevel;
  namespace: LogNamespace;
  correlationId: string;
  
  // Content
  message: string;
  details: Record<string, any>;
  
  // Performance metrics (enhanced)
  duration?: number; // Backward compatibility
  memoryUsage?: number; // Backward compatibility
  performance?: PerformanceMetrics; // Enhanced performance data
  
  // Error information
  error?: ErrorInfo;
  
  // Context information
  userId?: string;
  sessionId?: string;
  environment: string;
}

/**
 * Error information interface
 */
export interface ErrorInfo {
  name: string;
  message: string;
  stack?: string;
  code?: string | number;
}

/**
 * Logger configuration interface
 */
export interface LoggerConfig {
  // Console output control
  consoleEnabled: boolean;
  consoleLevel: UniversalLogLevel;
  
  // Sentry integration
  sentryEnabled: boolean;
  sentryLevel: UniversalLogLevel; // Minimum level to send to Sentry
  sentryIncludeWarnings: boolean; // Whether to include WARNING level logs
  
  // Performance monitoring
  performanceTracking: boolean;
  
  // Security
  sensitiveDataFiltering: boolean;
  
  // Queue management
  queueEnabled: boolean;
  maxLogEntries: number;
  queueConfig: QueueConfig;
  
  // Environment
  environment: string;
}

/**
 * Environment configuration interface
 */
export interface EnvironmentConfig {
  environment: string;
  isDevelopment: boolean;
  isProduction: boolean;
  isTest: boolean;
  logLevel: UniversalLogLevel;
  enableConsoleOutput: boolean;
  enableSentryIntegration: boolean;
  enablePerformanceMetrics: boolean;
  enableSensitiveDataFiltering: boolean;
  maxLogEntries: number;
}

/**
 * Operation timing interface
 */
export interface OperationTiming {
  operationId: string;
  startTime: number;
  endTime?: number;
  duration?: number;
  startMemory?: MemoryUsage;
  endMemory?: MemoryUsage;
  memoryDelta?: MemoryDelta;
}

/**
 * Queue configuration for async log processing
 */
export interface QueueConfig {
  enabled: boolean;
  maxSize: number;
  processingBatchSize: number;
  processingIntervalMs: number;
  overflowStrategy: 'drop-oldest' | 'drop-newest' | 'backpressure';
  enableMetrics: boolean;
}

/**
 * Memory usage interface
 */
export interface MemoryUsage {
  used: number;
  total: number;
  percentage: number;
  // Node.js-style memory info for test compatibility
  heapUsed: number;
  heapTotal: number;
  external: number;
  heapLimit?: number;
  timestamp: number;
}

/**
 * Memory delta interface for tracking memory changes
 */
export interface MemoryDelta {
  usedDelta: number;
  totalDelta: number;
  timeDelta: number;
  usedDeltaMB: number;
}

/**
 * Memory threshold check result
 */
export interface MemoryThresholdCheck {
  isWarning: boolean;
  isCritical: boolean;
  level: 'normal' | 'warning' | 'critical';
  message?: string;
}

/**
 * Logger statistics interface
 */
export interface LoggerStats {
  queueLength: number;
  isProcessing: boolean;
  logLevel: UniversalLogLevel;
  loggingEnabled: boolean;
  totalLogsSent: number;
  errorCount: number;
  warningCount: number;
  environmentConfig: EnvironmentConfig;
  memoryUsage: MemoryUsage;
  // Additional properties for test compatibility
  totalLogEntries: number;
  queueSize: number;
}

/**
 * Namespace logger options
 */
export interface NamespaceLoggerOptions {
  namespace: LogNamespace;
  defaultCorrelationId?: string;
  userContext?: {
    userId?: string;
    sessionId?: string;
  };
}

/**
 * Log method signature type
 */
export type LogMethod = (
  message: string,
  details?: Record<string, any>,
  correlationId?: string,
  error?: Error
) => void;

/**
 * Operation logger method signature type
 */
export type OperationLogMethod = (
  operationName: string,
  details?: Record<string, any>
) => string; // Returns correlation ID

/**
 * Logger instance interface
 */
export interface IUniversalLogger {
  // Core logging methods
  error: LogMethod;
  warn: LogMethod;
  info: LogMethod;
  debug: LogMethod;
  
  // Operation tracking
  startOperation: OperationLogMethod;
  endOperation: (correlationId: string, details?: Record<string, any>) => void;
  
  // Function timing
  timeFunction<T>(
    functionName: string,
    fn: () => T,
    namespace?: LogNamespace,
    details?: Record<string, any>
  ): T;
  
  timeAsyncFunction<T>(
    functionName: string,
    fn: () => Promise<T>,
    namespace?: LogNamespace,
    details?: Record<string, any>
  ): Promise<T>;
  
  getPerformanceTimestamp(): number;
  calculateDuration(startTime: number, endTime?: number): number;
  
  // Configuration
  getConfig(): LoggerConfig;
  updateConfig(config: Partial<LoggerConfig>): void;
  
  // Statistics
  getStats(): LoggerStats;
  
  // Queue management
  flush(): Promise<void>;
  clear(): void;
  
  // Correlation ID management
  setRequestHeaders(headers: Record<string, string> | null): void;
  getCurrentRequestHeaders(): Record<string, string> | null;
  getCurrentCorrelationIdContext(): any;
  clearCorrelationIdContext(): void;
  createChildCorrelationId(operationName: string): string;
}

/**
 * Namespace logger interface
 */
export interface INamespaceLogger extends IUniversalLogger {
  readonly namespace: LogNamespace;
} 